import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import api from '../../lib/api';
import { 
  MessageCircle, 
  Send, 
  X, 
  Loader2,
  Circle,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/utils';
import { logger } from '../../lib/logger';

export default function CustomerSupportChat({ open, onClose }) {
  const { user } = useAuth();
  const { lastMessage } = useWebSocket();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (open) {
      fetchThread();
    }
  }, [open]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Listen for WebSocket updates
  useEffect(() => {
    if (lastMessage && lastMessage.type === 'support.new_message') {
      // Refresh thread to get new message
      fetchThread();
    }
  }, [lastMessage]);

  const fetchThread = async () => {
    setLoading(true);
    try {
      const response = await api.get('/support/thread');
      setThread(response.data);
      setMessages(response.data.messages || []);
      
      // Mark as read
      await api.post('/support/mark-read', {});
    } catch (error) {
      logger.error('Failed to load support thread:', error);
      toast.error('Failed to load support chat');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      return;
    }
    
    setSending(true);
    try {
      await api.post('/support/send-message', {
        message: newMessage.trim()
      });
      
      setNewMessage('');
      // Refresh thread to show new message
      await fetchThread();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to send message'));
    } finally {
      setSending(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl">
        <CardHeader className="border-b bg-gradient-to-r from-red-500 to-rose-500 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white">
              <MessageCircle className="w-5 h-5" />
              Customer Support
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm bg-white/20 px-3 py-1 rounded-full">
                <Circle className="w-2 h-2 fill-emerald-400 text-emerald-400" />
                <span>Support Team</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[400px] max-h-[500px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-red-500" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageCircle className="w-16 h-16 text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No messages yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Start a conversation with our support team
              </p>
            </div>
          ) : (
            messages.map((msg) => {
              const isOwnMessage = msg.sender_user_id === user.id;
              const isPlatformAdmin = msg.sender_institution_id === null;
              
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-3 ${
                      isOwnMessage
                        ? 'bg-red-500 text-white'
                        : isPlatformAdmin
                        ? 'bg-blue-100 text-slate-900'
                        : 'bg-slate-100 text-slate-900'
                    }`}
                  >
                    {!isOwnMessage && (
                      <p className="text-xs font-medium mb-1 opacity-80">
                        {msg.sender_name}
                        {isPlatformAdmin && (
                          <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white rounded text-xs">
                            Support Team
                          </span>
                        )}
                      </p>
                    )}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {msg.message_text}
                    </p>
                    <p className={`text-xs mt-1 ${isOwnMessage ? 'text-white/70' : 'text-slate-500'}`}>
                      {formatTimestamp(msg.timestamp)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        <div className="border-t p-4 bg-slate-50 flex-shrink-0">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              disabled={sending}
              className="flex-1"
              autoFocus
            />
            <Button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="bg-red-500 hover:bg-red-600"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
          <p className="text-xs text-slate-500 mt-2">
            Our support team typically responds within a few hours
          </p>
        </div>
      </Card>
    </div>
  );
}
